/**
 * Utility functions for JWT token operations
 */

export interface JwtPayload {
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * Decode JWT token without verification
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const decoded = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if token is expired
 * @param token JWT token string
 * @returns true if token is expired or invalid
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) {
    return true;
  }

  return Date.now() >= payload.exp * 1000;
}

/**
 * Check if token should be refreshed (at 2/3 of its lifetime)
 * @param token JWT token string
 * @returns true if token should be refreshed
 */
export function shouldRefreshToken(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp || !payload.iat) {
    return true; // If we can't determine, refresh to be safe
  }

  const issuedTime = payload.iat * 1000;
  const tokenLifetime = payload.exp * 1000 - issuedTime;
  return Date.now() >= issuedTime + (tokenLifetime * 2 / 3);
}

/**
 * Get time until token expires in milliseconds
 * @param token JWT token string
 * @returns milliseconds until expiration, or 0 if expired/invalid
 */
export function getTimeUntilExpiration(token: string): number {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) {
    return 0;
  }
  
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const timeUntilExpiration = expirationTime - currentTime;
  
  return Math.max(0, timeUntilExpiration);
}

/**
 * Get detailed token timing information
 * @param token JWT token string
 * @returns Object with timing information or null if invalid
 */
export function getTokenTimingInfo(token: string): {
  issuedAt: Date;
  expiresAt: Date;
  refreshAt: Date;
  currentTime: Date;
  timeUntilExpiration: number;
  timeUntilRefresh: number;
  tokenLifetime: number;
  timeUntilExpirationFormatted: string;
  timeUntilRefreshFormatted: string;
  tokenLifetimeFormatted: string;
} | null {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp || !payload.iat) {
    return null;
  }
  
  const issuedTime = payload.iat * 1000;
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const tokenLifetime = expirationTime - issuedTime;
  const refreshThreshold = issuedTime + (tokenLifetime * 2 / 3);
  
  const timeUntilExpiration = expirationTime - currentTime;
  const timeUntilRefresh = refreshThreshold - currentTime;
  
  // Format time helper
  const formatTime = (ms: number): string => {
    if (ms < 0) return 'Đã qua';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };
  
  return {
    issuedAt: new Date(issuedTime),
    expiresAt: new Date(expirationTime),
    refreshAt: new Date(refreshThreshold),
    currentTime: new Date(currentTime),
    timeUntilExpiration,
    timeUntilRefresh,
    tokenLifetime,
    timeUntilExpirationFormatted: formatTime(timeUntilExpiration),
    timeUntilRefreshFormatted: formatTime(timeUntilRefresh),
    tokenLifetimeFormatted: formatTime(tokenLifetime)
  };
}

